/** UI labels in English. Central catalog for visible strings. */
export const en = {
  common: {
    loading: 'Loading...',
    loadingPage: 'Loading page...',
    saving: 'Saving...',
    save: 'Save',
    cancel: 'Cancel',
    close: 'Close',
    retry: 'Retry',
    confirm: 'Confirm',
    noData: 'No data available',
    noDescription: 'No description',
    changeCount: (count: number) => (count <= 1 ? `${count} change` : `${count} changes`),
    unsavedChangeCount: (count: number) =>
      count <= 1 ? '1 unsaved change' : `${count} unsaved changes`,
    conflictExists: 'This combination already exists for a run.',
    insufficientRights: 'Insufficient rights for this action.',
    search: 'Search...',
  },
  auth: {
    forbiddenTitle: 'Access denied',
    forbiddenMessage: 'You do not have permission to access this page.',
    forbiddenBack: 'Back to home',
    loginInProgress: 'Signing in...',
    loginButton: 'Sign in',
  },
  login: {
    title: 'Sign in',
    subtitle: 'Access your personal workspace',
    incorrectCredentials: 'Incorrect credentials. Please try again.',
    username: 'Username',
    usernamePlaceholder: 'Enter your username',
    usernameAriaLabel: 'Username',
    password: 'Password',
    passwordPlaceholder: 'Enter your password',
    passwordAriaLabel: 'Password',
    forgotPassword: 'Forgot password?',
  },
  branding: {
    tagline: 'Polyurethane foam formulation and production',
    description: 'ROGITEX platform for FoamKit formulation, process and costing',
    loginSubtitle: 'Access your formulation workspace',
    welcomeMessage: 'Welcome to MONARQUE',
    featureUsersTitle: 'User management',
    featureUsersDescription: 'Full CRUD for users with roles and permissions',
    featureItemsTitle: 'Item management',
    featureItemsDescription: 'Modern CRUD interface for item management',
    featureJwtTitle: 'JWT authentication',
    featureJwtDescription: 'Auth system with JWT and refresh tokens',
  },
  layout: {
    settings: 'Settings',
    logout: 'Sign out',
    userFallback: 'User',
  },
  adminPage: {
    title: 'Administration',
    subtitle: 'Full system, user and security management',
    tabUsers: 'Users',
    tabRolesPermissions: 'Roles & permissions',
    tabSessions: 'Sessions',
    tabAudit: 'Audit',
  },
  foam: {
    grossTotals: 'Gross totals',
    gasLoss: 'Gas loss',
    netTotals: 'Net totals',
    fullRise: 'Full rise',
    allCategories: 'All categories',
    allSuppliers: 'All suppliers',
    noActiveRun: 'No active run',
    noActiveRunHint: 'Select or create a run to get started.',
    noRunSelected: 'No run selected.',
    noMachineSelected: 'No machine selected for this run.',
    selectMachineFallplate: 'Select a machine to display the fallplate profile.',
    selectMachineForRun: 'Select a machine for this run',
    formulationSaved: 'Formulation saved',
    testDataSaved: 'Test data saved',
    runSaved: 'Run saved',
    noActiveRunError: 'No active run',
    noIngredientsInFormulation: 'No ingredients in this formulation.',
    noActiveIngredients: 'No active ingredients in the formulation.',
  },
  fallplate: {
    fpLengthsUnavailable:
      'Machine FP lengths unavailable. Configure the machine to display the profile.',
    dragFpHint: 'Drag the red points vertically to adjust FP heights.',
  },
  chemicalCatalogue: {
    catalogueTitle: 'Catalogue',
    filters: 'Filters',
    products: 'Products',
    search: 'Search',
    searchPlaceholder: 'Name, type, supplier...',
    noMatch: 'No products match your search.',
    empty: 'No products.',
    productReferential: 'Product referential',
    insertInFormulation: 'Insert into formulation',
    replaceProduct: 'Replace product',
    removeFromFormulation: 'Remove from formulation',
    formulationActions: 'Formulation',
    physicalPump: 'Physical pump',
    insertModeHint: 'Choose a category and product, then insert it into the formulation.',
    replaceModeTitle: (typeName: string) => `Replace ${typeName}`,
    replaceActionHint: 'Choose a replacement product, or remove the stream from the formulation.',
    cancelReplace: 'Back to insert',
    chemicalAlreadyInFormulation: 'This product is already in the formulation.',
    formulationFull: 'The formulation already has 25 chemical streams.',
    cannotRemoveRequiredType:
      'This stream cannot be removed. At least one of each required type must remain.',
    wrongChemicalTypeForReplace: 'The replacement product must be the same chemical type.',
    noPumpSelected: 'No pump selected',
    pumpSelected: (pumpId: number) => `Pump ${String(pumpId).padStart(2, '0')} selected`,
    selectPump: 'Select pump',
    selectPumpTitle: 'Select pump',
    selectPumpHint: 'Optional: assign a physical pump to the next inserted product.',
    clearPumpSelection: 'Clear pump selection',
    category: 'Category',
    supplier: 'Supplier',
    name: 'Name',
    description: 'Description',
    viscosityCp: 'Viscosity, cp',
    pricePerKg: 'Price / kg',
    newProductSave: (typeName: string) => `New ${typeName}. Save to add.`,
    noSelectionSave: 'No selection. Save to add.',
    editProduct: (id: number) => `Editing product #${id}`,
    newProduct: 'New product',
    saveTooltip: 'Save',
    cancelEditsTooltip: 'Discard changes',
    deleteTooltip: 'Delete',
    deleteTitle: 'Delete product',
    saveProductFailed: 'Failed to save product',
    deleteFailed: 'Failed to delete',
    productAdded: 'Product added',
    productUpdated: 'Product updated',
    productDeleted: 'Product deleted',
    deleteConfirm: 'This action cannot be undone. Continue?',
    productNotFound: 'Product not found. Refresh the catalogue and try again.',
  },
  pump: {
    configured: 'Pump configured',
    removed: 'Pump removed',
    createFailed: 'Failed to create pump',
    updateFailed: 'Failed to update pump',
    deleteFailed: 'Failed to delete pump',
  },
  countries: {
    rateUpdateFailed: 'Failed to update rate',
    exchangeRateUpdated: 'Exchange rate updated',
    applyRate: 'Apply',
    currency: (code: string) => `Currency: ${code}`,
  },
  runs: {
    noMatch: 'No runs match your search.',
    empty: 'No runs recorded yet.',
    loadingRuns: 'Loading runs...',
    createFailed: 'Failed to create run',
    createSuccess: 'New run created',
    createButton: 'Create run',
    saveFailed: 'Failed to save run',
    noRunSelected: 'No run selected',
    loading: 'Loading...',
    viewRuns: 'View runs',
    searchPlaceholder: 'Search runs (grade, type, machine, no.)...',
    loadFailed: 'Unable to load runs.',
    loadActiveRunFailed: 'Unable to load the active run.',
    runNotFound: 'Run not found.',
    runUnavailable: 'Run unavailable',
    showMore: (loaded: number, total: number) => `Show more (${loaded}/${total})`,
  },
  database: {
    searchPlaceholder: 'Search chemicals...',
    noMatch: 'No chemicals match your search.',
  },
  startNewRun: {
    title: 'New run',
    foamType: 'Foam type',
    machineRequired: 'Machine required',
    typeRequired: 'Type required',
    runNo: 'Run no.',
  },
  process: {
    notesPlaceholder: 'Production notes for this run...',
    machineRunLabel: 'Run machine',
    currentRun: (name: string) => `Current run: ${name}`,
    machineTypeLabel: 'Machine type',
    machineNameLabel: 'Machine name',
    fpSectionsLabel: 'FP sections, Length cm',
    fpSection: (slotNo: number) => `FP section ${slotNo}`,
    addSection: 'Add',
    readOnlyMachine: 'Read-only. Only administrators can edit machine configuration.',
    noMachinesInReferential: 'No machines available in the referential.',
    chooseMachineFallplate: 'Choose a machine to display its fallplate configuration.',
    machineConfigSaved: 'Machine configuration saved',
    machineUpdateFailed: 'Failed to update machine',
    fpSectionUpdateFailed: 'Failed to update FP section',
    fpSectionDeleteFailed: 'Failed to delete FP section',
    exitFpEditMode: 'Exit edit mode',
    editFpSections: 'Edit FP sections',
    deleteFpSection: (slotNo: number) => `Remove FP section ${slotNo}`,
    riseProfileEditHint: 'To edit rise profile, click and drag a point left or right.',
    timeSeconds: 'Time, seconds',
    percentRise: '% rise',
  },
  testHistory: {
    noRuns: 'No runs available',
    loadingRuns: 'Loading runs...',
    loadRunsFailed: 'Failed to load run list.',
    noComparableRuns: 'No comparable runs found for this grade and foam type.',
    noDataForProperty: 'No data to display for this property in the selected range.',
    sequenceForRun: (gradeRef: string, foamTypeSuffix: string) =>
      `Sequence for ${gradeRef}${foamTypeSuffix}`,
    comparableSequence: 'Comparable run sequence',
    selectEffect: 'Select effect',
    runsAvailable: (count: number) => `${count} run(s) available`,
    searchGrade: (gradeRef: string) => ` (search: ${gradeRef})`,
    loadRunsForRangeFailed: 'Unable to load runs for this range.',
  },
  admin: {
    noUsers: 'No users available',
    noSessions: 'No active sessions',
    noAuditEvents: 'No events recorded',
    noSecurityAlerts: 'No active security alerts',
    noDataReturned: 'No data returned.',
    noSessionsForUser: 'No active sessions for this user',
    rolesLoadFailed: 'Unable to load roles.',
    insufficientRights: 'Insufficient rights (ViewUsers permission required).',
    unknownError: 'Unknown error.',
    permissionsLoadFailed: 'Error loading permissions.',
    rolesTitle: 'Roles',
    permissionsByCategory: 'Permissions by category',
    permissionsIncluded: (count: number) => `Included permissions (${count})`,
    roleValue: (name: string, value: number) => `${name} (value ${value})`,
    roleFallback: (value: number) => `Role ${value}`,
    readOnlyReferential:
      'Read-only referential view: roles and permissions are defined in the backend system (/api/admin/roles, /api/admin/permissions). The user grid allows assigning roles individually.',
    permissionsAdminOnly:
      'Full permission details by category are reserved for Admin accounts and above. You can still see the role list if you have the ViewUsers permission.',
    permissionsMatrixHint: 'Sign in with an Administrator account to display this detailed matrix.',
    success: 'Success',
    failure: 'Failure',
    failures: 'Failures',
    criticalEventsRecorded: 'All critical events are recorded:',
    user: 'User',
    role: 'Role',
    locked: 'Locked',
    createdOn: 'Created on',
    failureCount: (count: number) => `${count} failure(s)`,
    unlock: 'Unlock',
    lock: 'Lock',
    lockUserTitle: (action: string, username: string) => `${action} user ${username}?`,
    searchUsersPlaceholder: 'Search by name, role, status...',
    lastActivity: 'Last activity',
    terminateSessionTitle: 'Terminate session',
    terminateSessionMessage: 'Terminate this session? The user will be signed out.',
    terminateSessionConfirm: (username: string) =>
      `Terminate session for ${username}? The user will be signed out.`,
    inProgress: 'In progress...',
    realtime: 'Real-time',
    lockedAccounts: 'Locked',
    systemStatus: 'System status',
    operational: 'Operational',
    securityAlerts: 'Security alerts',
    lockedAccountsCount: (count: number) => `${count} locked account(s)`,
    multipleFailedLogins: 'Multiple failed login attempts',
    suspiciousActivity: 'Suspicious activity detected',
    roleDistribution: 'Role distribution',
    severity: 'Severity',
    category: 'Category',
    auditJournal: 'Audit & activity log',
    eventsCount: (count: number) => `${count} events`,
    failuresCount: (count: number) => `${count} failures`,
    totalEvents: 'Total events',
    categories: 'Categories',
    eventType: 'Event type',
    allCategories: 'All categories',
    allSeverities: 'All severities',
    period: 'Period',
    lastHour: 'Last hour',
    last24Hours: 'Last 24 hours',
    previous: 'Previous',
    pageOf: (page: number, totalPages: number, totalCount: number) =>
      `Page ${page} of ${totalPages} (${totalCount} events)`,
    auditSystemActive: 'Audit system active',
    auditEventsList: [
      'Sign-ins and sign-outs (success/failure)',
      'Role and permission changes',
      'Account lock and unlock',
      'Session management (create/terminate)',
      'CRUD operations on users and items',
      'Unauthorized access attempts',
    ],
    anomaliesDetected: 'Anomalies detected:',
    multipleIpUsers: (count: number) => `${count} user(s) signed in from multiple IPs`,
    suspiciousIps: (count: number) => `${count} IP(s) with suspicious activity (>5 sessions)`,
    status: 'Status',
    active: 'Active',
    alerts: 'Alerts',
    userManagement: 'User management',
    newUser: 'New user',
    edit: 'Edit',
    deleteUser: 'Delete user',
    deleteUserConfirm: (username: string) => `Delete user ${username}?`,
    editUser: 'Edit user',
    username: 'Username',
    activeSessionsTitle: (username: string) => `Active sessions - ${username}`,
    activeSessionCount: (count: number) => `${count} active session(s) for this user`,
    device: 'Device',
    close: 'Close',
    totalUsers: 'Total users',
    activeCount: (count: number) => `${count} active`,
    activeSessions: 'Active sessions',
    actionRequired: 'Action required',
    allClear: 'All clear',
    attention: 'Warning',
    allTypes: 'All types',
    last7Days: 'Last 7 days',
    last30Days: 'Last 30 days',
    allTime: 'All time',
    searchAuditLogs: 'Search logs...',
    searchSessions: 'Search by user, IP...',
    anomalyCount: (count: number) => `${count} anomaly(ies)`,
    next: 'Next',
    dateTime: 'Date & time',
    critical: 'Critical',
  },
  unsavedChanges: {
    title: 'Unsaved changes',
    body: (label: string) => `${label}. If you leave now, your changes will be lost.`,
    leaveWithoutSaving: 'Leave without saving',
    saveAndLeave: 'Save and leave',
  },
  items: {
    empty: 'No items available. Start by creating your first item.',
    emptyTable: 'No items available',
    createdSuccess: 'Item created successfully',
    createError: 'Error while creating',
    updatedSuccess: 'Item updated successfully',
    updateError: 'Error while updating',
    deletedSuccess: 'Item deleted successfully',
    deleteTitle: 'Delete this item?',
    deleteMessage: (name: string) =>
      `Are you sure you want to delete "${name}"? This action cannot be undone.`,
    delete: 'Delete',
    createdAt: 'Created at',
    createDialogTitle: 'Create a new item',
    creating: 'Creating...',
    create: 'Create',
    updating: 'Updating...',
    update: 'Update',
    deleteError: 'Error while deleting',
    pageTitle: 'Item management',
    totalCount: (count: number) => `${count} item${count === 1 ? '' : 's'} total`,
    newItem: 'New item',
    searchPlaceholder: 'Search by name or description...',
    editDialogTitle: 'Edit item',
    nameRequired: 'Name *',
  },
  backend: {
    unreachable: 'Backend is unreachable. Displayed data may not be up to date.',
    reconnecting: 'Reconnecting...',
    hideBanner: 'Hide banner',
    connectionFailedTitle: 'Unable to connect to server',
    connectionFailedDescription:
      'Unable to connect to the backend. Please verify the server is running and accessible.',
    contactAdmin: 'If the problem persists, contact your system administrator',
  },
  network: {
    backOnline: 'Connection restored! You are back online.',
    offline: 'You are offline. Changes will sync once the connection is restored.',
  },
  dataTable: {
    searchResults: (count: number, term: string) => `${count} result(s) for "${term}"`,
    refresh: 'Refresh',
    export: 'Export',
    exportCsv: 'Export as CSV',
    exportJson: 'Export as JSON',
    edit: 'Edit',
    delete: 'Delete',
    actions: 'Actions',
    rowsPerPage: 'Rows per page:',
    displayedRows: (from: number, to: number, count: number) => `${from}-${to} of ${count}`,
  },
  monarqueCard: {
    collapse: 'Collapse',
    expand: 'Expand',
  },
  numberField: {
    increase: 'Increase',
    decrease: 'Decrease',
  },
  errorBoundary: {
    title: 'Oops! Something went wrong',
    message:
      'We are sorry, but something went wrong. Our team has been notified and is working on a fix.',
    goHome: 'Back to home',
  },
  userSettings: {
    pageTitle: 'Account settings',
    pageSubtitle: 'Manage your account information',
    currentAccount: 'Current account',
    changeUsername: 'Change username',
    changePassword: 'Change password',
    success: 'Success',
    newUsername: 'New username',
    usernameHint:
      'Username must be 3-50 characters (letters, numbers, hyphens, dots and underscores only)',
    updateUsername: 'Update username',
    usernameUpdatedLogout:
      'Username updated successfully. Please sign in again with your new username.',
    usernameUpdatedPending:
      'Your username was updated successfully. You will be signed out in a few seconds to apply the changes...',
    usernameUpdateError: 'Error updating username',
    passwordUpdateError: 'Error updating password',
    passwordUpdated: 'Your password was updated successfully',
    currentPassword: 'Current password',
    newPassword: 'New password',
    confirmNewPassword: 'Confirm new password',
    updatePassword: 'Update password',
    securityCriteria: 'Security criteria:',
    usernameRequired: 'Username is required',
    usernameMinLength: 'Username must be at least 3 characters',
    usernameMaxLength: 'Username cannot exceed 50 characters',
    usernameInvalidChars:
      'Username may only contain letters, numbers, hyphens, dots and underscores',
    usernameSameAsOld: 'New username is the same as the current one',
    passwordRequired: 'Password is required',
    passwordCriteriaNotMet: 'Password does not meet all security criteria',
    currentPasswordRequired: 'Current password is required',
    passwordsDoNotMatch: 'Passwords do not match',
    passwordMin8: 'At least 8 characters',
    passwordUppercase: 'One uppercase letter',
    passwordLowercase: 'One lowercase letter',
    passwordDigit: 'One digit',
    passwordSpecial: 'One special character',
  },
  validation: {
    usernameRequired: 'Username is required',
    minChars: (n: number) => `Minimum ${n} characters`,
    maxChars: (n: number) => `Maximum ${n} characters`,
    passwordRequired: 'Password is required',
    oldPasswordRequired: 'Current password is required',
    newPasswordRequired: 'New password is required',
    confirmRequired: 'Confirmation is required',
    passwordComplexity:
      'Must contain at least one uppercase letter, one lowercase letter, one digit and one special character',
    passwordsMismatch: 'Passwords do not match',
    usernamePattern: 'Letters, numbers, hyphens and underscores only',
    roleRequired: 'Role is required',
    roleInvalid: 'Invalid role',
    nameRequired: 'Name is required',
    minOneChar: 'Minimum 1 character',
  },
  activeRun: {
    changeAriaLabel: 'Change active run',
  },
} as const;

export type Locale = typeof en;
